// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Collapse, IconButton, Typography } from "@mui/material"
import { FC, useContext, useEffect, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from "../common/CustomModal"
// others
import { UpdateCaseTypeInput, UpdateLawTypeInput, useUpdateCaseTypeMutation } from '../../../generated'
import { GENERAL_INFORMATION } from "../../constants"
import { AppContext, AuthContext } from "../../context"
import { ActionType } from "../../context/AppContextReducer"
import { DispatchAndState, ErrorException } from "../../interfaceTypes"
import { titleize } from "../../utils"
import { createLawTypeValidationSchema } from '../../validationSchema'
import { LawTypeAction, LawTypeActionType, LawTypeState } from "./LawTypeReducer"

export const ViewCurrentCaseType: FC<DispatchAndState<LawTypeState, LawTypeAction>> = ({ state, dispatch, refetch }) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const { isEdit, dispatch: appDispatch } = useContext(AppContext)
  const { isAdmin } = useContext(AuthContext)
  const { currentCaseType } = state || {}
  const { id, name } = currentCaseType || {}

  const handleClose = () => {
    appDispatch({ type: ActionType.SET_IS_EDIT, isEdit: false })
    dispatch({ type: LawTypeActionType.SET_CURRENT_CASE_TYPE, currentCaseType: null })
    setIsEditOpen(false)
    reset()
  }

  const methods = useForm<UpdateLawTypeInput>({
    mode: 'all',
    resolver: yupResolver(createLawTypeValidationSchema),

    defaultValues: {
      name: titleize(name || ''),
    }
  })

  const { handleSubmit, reset, setValue } = methods;

  const [updateCaseType, { loading }] = useUpdateCaseTypeMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions;
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          })
      }
    },

    onCompleted(data) {
      if (data) {
        const { updateCaseType: { response, caseType } } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && caseType) {
            Alert.success('Case type updated successfully')
            dispatch({ type: LawTypeActionType.SET_CURRENT_CASE_TYPE, currentCaseType: caseType })
            resetFields()
            setIsEditOpen(false);
            refetch && refetch()
          }
        }
      }
    }
  })

  const onSubmit: SubmitHandler<UpdateCaseTypeInput> = async (data: UpdateCaseTypeInput) => {
    const { name } = data

    if (id) {
      await updateCaseType({
        variables: {
          updateCaseTypeInput: {
            id,
            name
          }
        }
      })
    }
  }

  useEffect(() => {
    name && setValue("name", name);
    // eslint-disable-next-line
  }, [name]);

  const resetFields = () => {
    reset({
      name: name || "",
    });
  }

  return (
    <CustomModal title="Case Type detail" isOpen={isEdit} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems='center'>
            <Typography variant="body2" fontSize='18px' fontWeight='medium'>
              {GENERAL_INFORMATION}
            </Typography>

            {isAdmin &&
              <Box>
                <IconButton color="primary" onClick={() => setIsEditOpen(!isEditOpen)} aria-label={isEditOpen ? 'back' : 'edit'}>
                  {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
                </IconButton>

                {isEditOpen &&
                  <IconButton color="primary" type='submit' disabled={loading} aria-label='save'>
                    <SaveIcon />
                  </IconButton>
                }
              </Box>
            }
          </Box>

          <Box marginTop={2} marginBottom={3}>
            <Collapse in={!isEditOpen}>
              <Typography variant="body2" fontSize='14px'>Title:</Typography>

              <Typography variant="h6" fontSize='16px' fontWeight='normal' textTransform='capitalize'>
                {titleize(name || '')}
              </Typography>
            </Collapse>

            <Collapse in={isEditOpen}>
              <CustomController
                controllerName="name"
                controllerLabel="Title"
                fieldType="text"
              />
            </Collapse>
          </Box>
        </form>
      </FormProvider>
    </CustomModal>
  )
}